import React from 'react'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { Button, Modal } from 'react-bootstrap'
import { useTranslation } from 'next-i18next'

export const useConfirmLeave = ({
  shouldWarn = false,
  desiredBackRoute = null,
  desiredNextRoute = null,
  currentRoute = null,
  excludedRoutes = []
}) => {
  const router = useRouter()
  const [isConfirm, setIsConfirm] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [nextRoute, setNextRoute] = useState(null)
  const [showBrowserPrompt, setShowBrowserPrompt] = useState(false)
  const { t } = useTranslation('common')

  // Use beforeunload to prevent closing the tab, refreshing the page or moving outside the Next app
  useEffect(() => {
    const handleWindowClose = (e) => {
      if (!shouldWarn) return
      if (
        (!showModal && showBrowserPrompt) ||
        (currentRoute && !nextRoute) ||
        (excludedRoutes.length && !excludedRoutes.includes(nextRoute))
      ) {
        e.preventDefault()
        const event = e || window.event
        return (event.returnValue = 'Are you sure you want to leave?')
      }
    }

    window.addEventListener('beforeunload', handleWindowClose)
    return () => {
      window.removeEventListener('beforeunload', handleWindowClose)
    }
  }, [shouldWarn, showModal, showBrowserPrompt, excludedRoutes, nextRoute])

  // Use routeChangeStart to prevent navigation inside of the Next app
  useEffect(() => {
    const onRouteChangeStart = (route) => {
      if (!shouldWarn || isConfirm) return

      let showCustomModal = route != currentRoute && !route.includes('/register')
      let promptBrowserDialog = false
      if (excludedRoutes.length) {
        showCustomModal = showCustomModal && !excludedRoutes.includes(route)
        promptBrowserDialog = !excludedRoutes.includes(route)
      }
      if (route === currentRoute) {
        promptBrowserDialog = true
      }
      if (promptBrowserDialog != showBrowserPrompt) {
        setShowBrowserPrompt(promptBrowserDialog)
      }
      setNextRoute(route)
      if (showCustomModal) {
        setShowModal(true)
        router.events.emit('routeChangeError')
        throw 'navigation aborted'
      }
    }

    router.events.on('routeChangeStart', onRouteChangeStart)
    const cleanUp = () => router.events.off('routeChangeStart', onRouteChangeStart)

    if (isConfirm) {
      if (!nextRoute) return
      if (desiredBackRoute) {
        router.push(desiredBackRoute)
      } else {
        router.push(nextRoute)
      }
      return cleanUp
    }

    return cleanUp
  }, [shouldWarn, excludedRoutes, showModal, isConfirm, router.events, nextRoute])

  const onCancel = () => {
    window.history.pushState({}, '', desiredNextRoute)
    setNextRoute(null)
    setShowModal(false)
  }

  const onConfirm = () => {
    setIsConfirm(true)
    setShowModal(false)
  }

  const ConfirmLeaveModal = () => (
    <Modal className="modal-kp5" show={showModal} onHide={() => setShowModal(false)} centered>
      <Modal.Header>
        <Modal.Title>{t('Unsaved_changes')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>{t('If_you_leave_this_page_text')}</p>
      </Modal.Body>
      <Modal.Footer>
        <Button
          id="ga-kp-getstarted-browserbackbutton-cancel"
          className="btn btn-outline-primary mr-2"
          onClick={onCancel}
        >
          {t('CANCEL')}
        </Button>
        <Button
          id="ga-kp-getstarted-browserbackbutton-leavepage"
          className="btn btn-primary"
          onClick={onConfirm}
        >
          {t('LEAVE_PAGE')}
        </Button>
      </Modal.Footer>
    </Modal>
  )

  return {
    ConfirmLeaveModal
  }
}
